/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { omit, get } from 'lodash';
import Raven from 'raven-js';
import { Icon, Message } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { prepareFloatForServer } from '../../../../../lib/numberFormatter';
import { Modal, Button, Color } from '../../../../../components';
import ServiceForm from './ServiceForm';

type EditServiceDialogProps = {
    intl: Object,
    trigger?: any,
    open: boolean,
    onClose: () => void,
    service: {
        id: string,
        name: string,
        nameVariants: { lv?: string, ru?: string, en?: string },
        duration: number,
        price: string,
        color: string,
    },
};

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.settings.services.form.existingsError',
        defaultMessage: 'This service does not exist',
    },
    nameExistingsError: {
        id: 'pages.settings.services.add.nameExistingsError',
        defaultMessage: 'Service with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const UPDATE_SERVICE_MUTATION = gql`
    mutation updateService($input: UpdateServiceInput!, $lang: String!) {
        updateService(input: $input) {
            service {
                id
                name(lang: $lang)
                nameVariants {
                    lv
                    ru
                    en
                }
                duration
                price
                color
            }
        }
    }
`;

const EditServiceDialog = ({
    trigger,
    open,
    onClose,
    service,
    intl,
}: EditServiceDialogProps) => {
    const currentLanguage = useSelector(
        ({ intl: intlState }) =>
            intlState.get('locale') || intlState.get('defaultLanguage'),
    );
    const languages = useSelector(({ intl: intlState }) =>
        intlState.get('languages'),
    );

    const buildDefaultValues = () =>
        service
            ? omit(service, ['__typename', 'id', 'name'])
            : {
                  nameVariants: (languages || []).reduce(
                      (acc, lang) => ({ ...acc, [lang]: '' }),
                      {},
                  ),
                  duration: null,
                  price: '',
                  color: '',
              };

    const methods = useForm({
        defaultValues: buildDefaultValues(),
    });

    const { reset, setError, handleSubmit, formState: { isSubmitting, isDirty, errors } } = methods;

    useEffect(() => {
        if (open && service) {
            reset(omit(service, ['__typename', 'id', 'name']));
        }
    }, [open, service, reset]);

    const [updateService] = useMutation(UPDATE_SERVICE_MUTATION);

    const onSubmit = async formData => {
        try {
            const duration = formData.duration.toString();
            const price = prepareFloatForServer(formData.price);
            const submitData = omit(formData, ['name', 'duration', 'price']);
            Object.assign(submitData, {
                nameVariants: omit(submitData.nameVariants, ['__typename']),
            });

            await updateService({
                variables: {
                    input: {
                        serviceId: service.id,
                        duration,
                        price,
                        ...submitData,
                    },
                    lang: currentLanguage,
                },
            });
            onClose();
        } catch (error) {
            Raven.captureException(error);

            if (error.graphQLErrors && error.graphQLErrors.length) {
                const graphQLError = error.graphQLErrors[0];
                if (
                    graphQLError.name === 'NotExists' &&
                    get(graphQLError, 'data.error') === 'ID_DOES_NOT_EXISTS'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.existingsError),
                    });
                    return;
                } else if (
                    graphQLError.name === 'AlreadyExists' &&
                    get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.nameExistingsError),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal size="small" trigger={trigger} open={open} onClose={onClose}>
                <Modal.Header>
                    {service && (
                        <>
                            <Color size={48} color={service.color} spaced="right" />
                            {service.name}
                        </>
                    )}
                </Modal.Header>
                <Modal.Content>
                    {errors.root && (
                        <Message error>
                            <Icon name="warning sign" />
                            {errors.root.message}
                        </Message>
                    )}
                    <ServiceForm onSubmit={onSubmit} />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        onClick={handleSubmit(onSubmit)}
                        primary
                    >
                        <Icon name="checkmark" />
                        <FormattedMessage
                            id="pages.settings.services.edit.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button disabled={isSubmitting} onClick={onClose} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.settings.services.edit.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
};

export default injectIntl(EditServiceDialog);
